












































































































import { Watch, Component, Prop, Vue } from 'vue-property-decorator'
import { Referral } from '@/api/model/referral'
import { Branch } from '@/api/model/branch'
import { PipelineAPI, PipelinePhaseAPI, ReferralAPI, SourceAPI } from '@/api/backend-client'
import { v4 as uuid } from 'uuid'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { generateChannelOptions, InputSelectOption } from '@/helpers/options'
import { namespace } from 'vuex-class'
import { User } from '@/api/model/user'
import { availableReferralChannels, ReferralChannel, ReferralStatus } from '@/api/types'
import { Source } from '@/api/model/source'
const pipelineConnections = namespace('pipelineConnections')

@Component
export default class DuplicateReferralModal extends Vue {
    @pipelineConnections.Getter readonly availablePipelines!: Pipeline[]
    @pipelineConnections.Action readonly loadPipelineConnections!: () => Promise<void>
    @Prop({ required: true }) readonly referral!: Referral
    @Prop({ type: Boolean, default: false }) value!: boolean
    visible: boolean = false
    loading: boolean = false
    selectedSource: Source = null
    selectedChannel: string = null
    selectedPipelineId: string = null
    selectedPipelinePhase: PipelinePhase = null
    selectedBranch: Branch = null
    selectedFromBranch: Branch = null
    selectedUser: User = null
    pipelines: Pipeline[] = []
    phases: PipelinePhase[] = []
    changeChannelLoading: boolean = true
    combinedSources: Source[] = []

    @Watch('value')
    onValueChanged(): void {
        this.visible = this.value
    }

    @Watch('visible')
    onVisibleChanged(): void {
        this.$emit('input', this.visible)
    }

    async mounted(): Promise<void> {
        await this.loadPipelines()
        await this.loadSources()
        this.autoSelectBranch()
        this.autoShowIfReferralWasCreated()
        this.autoSelectSource()
        this.autoSelectChannel()
    }

    private autoSelectSource(): void {
        this.selectedSource = this.combinedSources.find(source => source.id === this.referral.newSource?.id)
    }

    private autoSelectChannel(): void {
        this.selectedChannel = availableReferralChannels().find(channel => channel === this.referral.channel)
    }

    autoShowIfReferralWasCreated(): void {
        if (window.keyvue.referralCreated) {
            window.keyvue.referralCreated = false

            if (confirm(this.$t('forms.duplicate-referral-form.confirm').toString())) {
                this.showModal()
            }
        }
    }

    showModal(): void {
        this.visible = true
    }

    hideModal(): void {
        this.visible = false
    }

    autoSelectBranch(): void {
        const branches = this.$currentUser.getAvailableBranches()

        if (branches.length === 1) {
            this.selectedFromBranch = branches[0]
        }
    }

    async createAdditionalReferral(): Promise<void> {
        this.loading = true
        await ReferralAPI.create({
            ...this.referral,

            contact: this.referral.contact.resourceIdentifier,
            company: this.$currentCompany.resourceIdentifier,
            id: uuid(),
            pipeline: this.selectedPipeline?.resourceIdentifier,
            phase: this.selectedPipelinePhase ? PipelinePhase.fromData({id: this.selectedPipelinePhase}).resourceIdentifier : null,
            referredBy: this.$currentUser.resourceIdentifier,
            referredByBranch: this.selectedFromBranch.resourceIdentifier,
            assignedTo: this.selectedUser ? this.selectedUser.resourceIdentifier : null,
            assignedToBranch: this.selectedBranch ? Branch.fromData(this.selectedBranch).resourceIdentifier : null,
            newSource: this.selectedSource?.resourceIdentifier,
            channel: this.selectedChannel,
            status: ReferralStatus.OPEN,
            statusReason: null,
            newStatusReason: null,
            nextActionAt: null,
            appointment: null,
            appointmentLocation: null,
            orderValue: null,
            leadCommission: null,
            nettoOrderValue: null,
            transportDate: null,
            productionDate: null,
            score: 0,

        } as unknown as Referral)
        this.loading = false
        this.hideModal()
    }

    async loadPipelines(): Promise<void> {
        this.loading = true
        await this.loadPipelineConnections()
        this.pipelines = await PipelineAPI.getOwnPipelines()
        this.loading = false
    }

    @Watch('selectedPipelineId')
    async loadSources(): Promise<void> {
        this.changeChannelLoading = true
        try {
            if (!this.selectedPipelineId) {
                this.combinedSources.splice(0)
                return
            }

            const sources = await SourceAPI.getAll({
                company: this.selectedPipeline.company.id,
                active: 1,
            })

            this.combinedSources.splice(0)
            this.combinedSources.push(...sources)

        } finally {
            this.changeChannelLoading = false
        }
    }

    @Watch('selectedPipelineId')
    async loadPhases(): Promise<void> {
        this.loading = true
        this.phases = (await PipelinePhaseAPI.getAll({ pipeline: this.selectedPipelineId}))
            .sort((a, b) => a.compareTo(b))
            .filter(phase => phase.active)
        this.loading = false
    }

    @Watch('selectedPipelineId')
    clearPhase(): void {
        this.selectedPipelinePhase = null
        this.selectedBranch = null
        this.selectedUser = null
    }

    @Watch('selectedPipelinePhase')
    clearBranch(): void {
        this.selectedBranch = null
        this.selectedUser = null
    }

    @Watch('selectedBranch')
    clearUser(): void {
        this.selectedUser = null
    }

    get availableChannelOptions(): InputSelectOption[] {
        return generateChannelOptions()
    }

    setSelectedSource(sourceId: string): void {
        this.selectedSource = this.combinedSources.find(source => source.id === sourceId)
    }

    get combinedPipelines(): Pipeline[] {
        return [
            ...this.availablePipelines,
            ...this.pipelines,
        ]
            .filter((pipeline, index, self) => self.findIndex(p => p.id === pipeline.id) === index)
            .filter(pipeline => pipeline.active)
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    get selectedPipeline(): Pipeline {
        return this.combinedPipelines.find(pipeline => pipeline.id === this.selectedPipelineId)
    }

    get pipelineOptions(): InputSelectOption[] {
        return this.combinedPipelines
            .filter(pipeline => pipeline.id !== this.referral.pipeline.id)
            .map(pipeline => ({
                label: pipeline.name,
                subtext: pipeline.company.name,
                value: pipeline.id,
            }))
    }

    get pipelinePhaseOptions(): InputSelectOption[] {
        return this.phases.map(phase => {
            return {
                label: phase.name,
                value: phase.id,
            }
        })
    }

    get availableFromBranches(): Branch[] {
        return this.$currentUser.getAvailableBranches()
    }

    get availableToBranches(): Branch[] {
        if (!this.selectedPipeline) {
            return []
        }

        return this.combinedPipelines
            .find(pipeline => pipeline.id === this.selectedPipelineId).branches
            .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
    }

    get availableToUsers(): User[] {
        if (!this.isOwnPipeline || !this.selectedBranch) {
            return []
        }

        return this.$currentCompany.getAvailableUsers({
            branch: this.selectedBranch,
        })
    }

    get isOwnPipeline(): boolean {
        if (!this.selectedPipeline) {
            return false
        }

        return this.combinedPipelines
            .some(pipeline => pipeline.id === this.selectedPipelineId && pipeline.company.id === this.$currentCompany.id)
    }

    get sourceOptions(): InputSelectOption[] {
        return this.combinedSources
            .filter(source => source.channel === this.selectedChannel)
            .map(source => ({
                label: source.name,
                value: source.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }
}
